import { Component, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';

import {
  of,
  Subscription,
  catchError,
  tap,
  switchMap,
  debounceTime,
  take, filter,
} from 'rxjs';

import { AuthUser } from '@shared/signup/models';
import {
  emailRegexp,
  USERNAME_PATTERN,
  NAME_PATTERN,
  ValidationService,
  OLD_USERNAME_PATTERN,
} from '@tablesready/common-ui';
import { TypePasswordModalComponent } from '../../modals';
import { FrontendModalService } from '@tablesready/frontend-modal';
import { UpdateUserInfoRequest } from '@shared/user/models';
import { AuthRestaurantService } from '@tablesready/frontend-store';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { UserApiService } from '@tablesready/frontend-auth';
import { map } from 'rxjs/operators';

@Component({
  selector: 'tablesready-change-name',
  templateUrl: './change-name.component.html',
})
export class ChangeNameComponent implements OnInit, OnDestroy {
  emailForm: UntypedFormGroup;
  restrictUserAlert = false;

  private readonly subscriptions = new Subscription();
  private user: AuthUser;

  constructor(
    private readonly modalController: FrontendModalService,
    private readonly fb: UntypedFormBuilder,
    private readonly alertsService: AlertsService,
    private readonly authRestaurantService: AuthRestaurantService,
    private readonly userApiService: UserApiService,
    private readonly validationService: ValidationService,
  ) {}

  ngOnInit(): void {
    this.subscriptions.add(this.authRestaurantService.select()
      .pipe(tap((user) => {
        this.user = user;
        this.emailForm = this.fb.group({
          name: [
            user.name || '',
            [
              Validators.required,
              Validators.pattern(NAME_PATTERN),
              Validators.maxLength(50),
            ],
          ],
          email: [
            user.email || '',
            [Validators.required, Validators.pattern(emailRegexp), Validators.maxLength(100)],
            [
              (control: AbstractControl) => this.validationService.emailUsedValidator(control.value, user._id),
              (control: AbstractControl) => this.validationService.validateEmail(control.value),
            ],
          ],
          username: [
            user.username || '',
            [
              Validators.required,
              Validators.minLength(4),
              Validators.maxLength(50),
              Validators.pattern(USERNAME_PATTERN.test(user.username || '') ? USERNAME_PATTERN : OLD_USERNAME_PATTERN),
            ],
            [(control: AbstractControl) => this.validationService.usernameUsedValidator(control.value, user._id)],
          ],
        });

        this.checkProfile();
      }))
      .subscribe());

    const FORM_DEBOUNCE = 500;
    const formSub = this.emailForm.valueChanges
      .pipe(debounceTime(FORM_DEBOUNCE))
      .subscribe(() => {
        this.validationService.validateForm(this.emailForm);
      });

    this.subscriptions.add(formSub);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  submit(): void {
    of({
      name: this.emailForm.get('name').value !== this.user.name,
      email: this.emailForm.get('email').value !== this.user.email,
      username: this.emailForm.get('username').value !== this.user.username,
    })
      .pipe(
        take(1),
        filter(changes => changes.name || changes.email || changes.username),
        switchMap((changes) => {
          if (changes.email || changes.username) {
            return this.modalController.create<TypePasswordModalComponent, string>(TypePasswordModalComponent);
          }

          return changes.name
            ? this.userApiService.update(this.emailForm.value).pipe(map(() => undefined))
            : of(undefined);
        }),
        filter(data => !!data),
        switchMap((password?: string) => {
          let data: UpdateUserInfoRequest = this.emailForm.value;
          if (password) {
            data = {
              ...data,
              username: data.username.toLowerCase(),
              email: data.email.toLowerCase(),
              password,
            };
          }

          return this.userApiService.update(data);
        }),
        tap(() => {
          this.alertsService.addSuccess('account info updated successfully.');
          this.emailForm.markAsPristine();
        }),
        catchError((error) => {
          this.alertsService.addError(error.message || String(error.error.error));

          return of(null);
        }),
      )
      .subscribe(() => {
        this.checkProfile();
      });
  }

  private checkProfile(): void {
    const emptyName = !this.user.name || this.user.name === '';
    const emptyEmail = !this.user.email || this.user.email === '';
    this.restrictUserAlert = emptyName || emptyEmail;

    if (this.restrictUserAlert && this.emailForm !== undefined) {
      const fieldNames = ['name', 'email'];
      fieldNames.forEach((fieldName) => {
        const control = this.emailForm.get(fieldName);
        control?.markAsTouched({ onlySelf: true });
      });
    }
  }
}
